import {
  createTheme,
  rem,
  type MantineColor,
  type MantineColorScheme,
  type MantineThemeColors,
  type MantineThemeOverride,
} from "@mantine/core";
import { createContext, useContext, useEffect } from "react";
import { ZedIndex } from "../../utils/zedIndex";
import cardClasses from "../variants/Card.module.css";
import chipClasses from "../variants/Chip.module.css";

// Init mantine theme:
const mantineTheme = createTheme({
  fontFamily: "'Inter Variable', sans-serif",
  colors: {
    dark: [
      "#ece9e2",
      "#d5d3cc",
      "#a5a9ab",
      "#6b6f70",
      "#2e3139",
      "#22252c",
      "#171a21",
      "#111319",
      "#0a0b10",
      "#05060a",
    ],
    blue: [
      "#eaf0ff",
      "#d2dbfc",
      "#a3b4f6",
      "#718af2",
      "#4767ee",
      "#2f52ed",
      "#2146ed",
      "#1538d3",
      "#0c31bd",
      "#0029a7",
    ],
    yellow: [
      "#FBEBD5",
      "#F9CF7E",
      "#ECB837",
      "#CFA02E",
      "#AC8425",
      "#88691B",
      "#644C10",
      "#4C390C",
      "#2F240B",
      "#1A1507",
      "#1A1507",
    ],
    red: [
      "#FBEAE6",
      "#F9CBC0",
      "#F9AE9B",
      "#F8876F",
      "#F74D37",
      "#D2241B",
      "#A91A14",
      "#780F0B",
      "#481308",
      "#251006",
      "#251006",
    ],
    orange: [
      "#FBEAE2",
      "#FCCBB4",
      "#FEAC83",
      "#FE8647",
      "#EA5D04",
      "#BB4702",
      "#A33D02",
      "#692702",
      "#3F1B05",
      "#211105",
    ],
    teal: [
      "#DBFAF6",
      "#CBF7F2",
      "#8CE6DF",
      "#77D1C8",
      "#67B7AF",
      "#539891",
      "#407873",
      "#305C58",
      "#21423F",
      "#152A27",
      "#0D1E1B",
      "#0D1716",
      "#0D1716",
    ],
    base: [
      "#FFFFFF",
      "#F7F8F8",
      "#EDEEEE",
      "#D5D7D8",
      "#BFC1C3",
      "#A5A9AB",
      "#8A8C8E",
      "#6B6F70",
      "#505253",
      "#3B3D3D",
      "#252626",
      "#151616",
    ],
  },
  primaryShade: 3,
  radius: {
    ml: rem(12),
  },
  components: {
    Badge: {
      defaultProps: {
        radius: "lg",
      },
    },

    Blockquote: {
      defaultProps: {
        p: "md",
        m: "lg",
        ta: "left",
      },
      styles: {
        root: {
          borderLeft: "none",
        },
      },
    },

    Button: {
      defaultProps: {
        fw: 500,
        loaderProps: {
          type: "oval",
        },
      },
    },

    Card: {
      classNames: cardClasses,
      defaultProps: {
        radius: "lg",
        shadow: "none",
        withBorder: true,
      },
    },

    Chip: {
      classNames: chipClasses,
      defaultProps: {
        radius: "md",
        variant: "filled",
        fw: 600,
      },
    },

    Fieldset: {
      styles: {
        root: {
          backgroundColor: "var(--mantine-color-default)",
        },
      },
    },

    Overlay: {
      defaultProps: {
        zIndex: ZedIndex.Overlay,
      },
    },

    Modal: {
      styles: {
        content: {
          backgroundColor: "var(--mantine-color-default)",
          border: "1px solid var(--mantine-color-default-border)",
        },
        header: {
          backgroundColor: "var(--mantine-color-default)",
        },
      },
    },

    ProgressRoot: {
      defaultProps: {
        radius: "var(--mantine-radius-sm)",
      },
    },

    PasswordInput: {
      classNames: {
        error: "form-error",
        input: "form-textinput",
      },
    },

    TextInput: {
      classNames: {
        error: "form-error",
        input: "form-textinput",
      },
      styles: {
        error: {},
        label: {
          marginBottom: "3px",
        },
      },
    },

    Title: {
      defaultProps: {
        fw: "normal",
      },
    },

    Textarea: {
      defaultProps: {
        size: "md",
      },
    },

    Popover: {
      defaultProps: {
        withArrow: true,
        arrowSize: 15,
        shadow: "sm",
      },
    },

    Tooltip: {
      defaultProps: {
        withArrow: true,
        offset: 10,
        color: "black",
      },
    },
  },
});

export const defaultTheme = {
  ...mantineTheme,
  primaryColor: "teal",
  secondaryColor: "orange",
} as ThemeProps;

export interface UpdateColorProps {
  colorHex: MantineColor;
  /**
   * Name of the color to be used as the identifier or a new color palette
   * and/or as the new color that override one of the theme
   * defaults (e.g. 'primaryColor').
   */
  colorName: string;
  /**
   * When true, will generate a new color palette corresponding to the provided
   * 'colorHex' prop.
   */
  generateColorPalette: boolean;
  /**
   * Valid mantine theme props that can be overriden.
   */
  overrideColorName?: "primaryColor" | "secondaryColor";
}

export interface ThemeProps extends MantineThemeOverride {
  colors: MantineThemeColors;
  secondaryColor: MantineColor;
}

interface ThemeContextProps {
  theme: ThemeProps;
  updateColors: (args: UpdateColorProps[]) => void;
  resetTheme: () => void;
  setForcedColorScheme: (colorScheme?: ForcedColorScheme) => void;
}

export type ForcedColorScheme = Exclude<MantineColorScheme, "auto">;

export const ThemeContext = createContext<ThemeContextProps>({
  theme: defaultTheme,
  updateColors: () => {},
  resetTheme: () => {},
  setForcedColorScheme: () => {},
});

export function useTheme() {
  return useContext(ThemeContext);
}

/**
 * Whole document only, mantine dark styles match on the <html> attribute so
 * this can't be scoped to a subtree. Saved preference is left alone.
 */
export function useForcedColorScheme(colorScheme: ForcedColorScheme) {
  const { setForcedColorScheme } = useTheme();

  useEffect(() => {
    setForcedColorScheme(colorScheme);
    return () => setForcedColorScheme(undefined);
  }, [colorScheme, setForcedColorScheme]);
}
